import cx from 'classnames'
import { Component } from 'preact'
import { Form, FormCheckbox } from '../form'
import Cols from '../cols/cols'
import Col from '../cols/col'
import { TextNode, Heading1, Caption } from '../textNodes'
import BurosferaVacancyFormImageUploader from './burosferaVacancyFormImageUploader'
import MobileFooter from './burosferaVacancyFormMobileFooter'
import BurosferaVacancyFormValidator from './validator'
import BurosferaVacancyAuthor from './burosferaVacancyAuthor'

import {
  Row,
  Label,
  Input,
  JobFormatSwitcher,
  JobDescriptionTextarea,
  DraftCaption,
} from './components'

import { capitalize } from 'lib/textHelper'
import { flushDraft } from './saveDraft'
import readNewDraft from './readNewDraft'

const SAVE_DRAFT_DELAY = 1000

export default class BurosferaVacancyForm extends Component {
  constructor(props) {
    super(props)

    const { children: _, ...state } = props

    this.state = state

    this.bindRootRef = el => this.el = el
    this.save = this.save.bind(this)
    this.onImageLoaded = this.onImageLoaded.bind(this)
    this.onImageDeleted = this.onImageDeleted.bind(this)
    this.onAuthorVisibleChange = this.onAuthorVisibleChange.bind(this);

    ['jobTitle', 'companyTitle', 'jobLocation', 'contactEmail', 'jobDescription'].forEach(field => {
      this[`on${capitalize(field)}Input`] = ({ value, target }) => this.setState({ [field]: target?.value || value, isDirty: true })
    });

    ['remote', 'office'].forEach(format => {
      this[`onJobFormat${capitalize(format)}`] = () => this.setState({ jobFormat: format, isDirty: true })
    })
  }

  get title() {
    return this.state.isPublished ? 'Вакансия' : 'Новая вакансия'
  }

  componentDidMount() {
    window.application.newPaybar ||= {}
    window.application.newPaybar.externalForm = this
  }

  componentDidUpdate() {
    if (!this.state.isDirty) return

    clearTimeout(this.saveDraftTimeout)

    this.saveDraftTimeout = setTimeout(this.save, SAVE_DRAFT_DELAY)
  }

  get isValid() {
    return this.validator.isValid
  }

  get validationMessage() {
    return this.validator.validationMessage
  }

  get validator() {
    return new BurosferaVacancyFormValidator(this.state)
  }

  async submit() {
    return flushDraft(this.state)
  }

  async save() {
    return this.submit()
      .then(() => this.setState({ isSaved: true, isDirty: false }))
      .catch(() => this.setState({ isSaved: false, isDirty: true }))
  }

  get data() {
    return { uid: this.state.uid }
  }

  async reset() {
    return readNewDraft()
      .then(state => this.setState({ ...state, isDirty: false, isSaved: false }))
  }

  focus() {
    this.el.scrollIntoView({ behavior: 'smooth' })
  }

  onImageLoaded({ imageId, imageUrl }) {
    this.setState({ imageId, imageUrl, isDirty: true })
  }

  onImageDeleted() {
    this.setState({ imageId: null, imageUrl: null, isDirty: true })
  }

  onAuthorVisibleChange({ value }) {
    this.setState({ authorVisible: value, isDirty: true })
  }

  get isJobLocationRequired() {
    return this.state.jobFormat === 'office'
  }

  render() {
    const {
      uid,
      jobTitle,
      companyTitle,
      jobFormat,
      jobLocation,
      contactEmail,
      jobDescription,
      imageUrl,
      authorVisible,
      authorName,
      authorEnglishName,
      authorOrganization,
      authorJob,
      authorCpd,
      authorAvatarUrl,
      isSaved,
    } = this.state

    const className = cx('burosferaVacancyForm', 'module', { is__saved: isSaved })

    return (
      <div className={ className } ref={ this.bindRootRef }>
        <Heading1 className="l">{ this.title }</Heading1>
        <Cols divisionProportions="12:4" transposeOnMobile>
          <Col>
            <Form className="burosferaVacancyForm-form">
              <div className="burosferaVacancyForm-fieldset">
                <Row>
                  <Label name="jobTitle" text="Профессия или роль по‑русски" />
                  <Input name="jobTitle" value={ jobTitle } onInput={ this.onJobTitleInput } />
                </Row>

                <Row>
                  <Label name="companyTitle" text="Название проекта или компании" />
                  <Input name="companyTitle" value={ companyTitle } onInput={ this.onCompanyTitleInput } />
                </Row>
              </div>

              <div className="burosferaVacancyForm-fieldset">
                <Row>
                  <Label name="jobFormat" text="Формат работы" />
                  <JobFormatSwitcher
                    value={ jobFormat }
                    onRemoteClick={ this.onJobFormatRemote }
                    onOfficeClick={ this.onJobFormatOffice } />
                </Row>

                <Row>
                  <Label name="jobLocation" text="Город" />
                  <Input name="jobLocation" value={ jobLocation } onInput={ this.onJobLocationInput } required={ this.isJobLocationRequired } />
                </Row>
              </div>

              <div className="burosferaVacancyForm-jobDescriptionRow">
                <Label name="jobDescription" text="<b>Описание вакансии</b>" />
                <TextNode>
                  Расскажите о задаче, ожиданиях, команде, графике.<br/>
                  <a href="/burosfera/hire/vacancy/howto/">Как написать хорошую вакансию</a>
                </TextNode>
                <JobDescriptionTextarea
                  uid={ uid }
                  value={ jobDescription }
                  onInput={ this.onJobDescriptionInput } />
                <Caption>
                  <a href="/burosfera/hire/vacancy/formatting/">❔ Форматирование</a>
                </Caption>
              </div>

              <div className="burosferaVacancyForm-imageRow">
                <Label name="image" text="Иллюстрация над описанием вакансии" />
                <BurosferaVacancyFormImageUploader
                  imageUrl={ imageUrl}
                  onLoaded={ this.onImageLoaded }
                  onDeleted={ this.onImageDeleted } />
              </div>

              <div className="burosferaVacancyForm-fieldset">
                <Row>
                  <Label name="contactEmail" text="Эл. почта для откликов" />
                  <Input name="contactEmail" value={ contactEmail } onInput={ this.onContactEmailInput } />
                </Row>

                <Row>
                  <FormCheckbox
                    name="authorVisible"
                    value={ authorVisible }
                    text="Указать меня как автора вакансии"
                    onChange={ this.onAuthorVisibleChange } />
                </Row>
              </div>
            </Form>
          </Col>

          <Col>
            <div className="sidenote burosferaVacancyForm-sidenote module">
              <div className="sidenote-text">
                <BurosferaVacancyAuthor
                  isVisible={ authorVisible }
                  authorName={ authorName }
                  authorEnglishName={ authorEnglishName }
                  authorOrganization={ authorOrganization }
                  authorJob={ authorJob }
                  authorCpd={ authorCpd }
                  authorAvatarUrl={ authorAvatarUrl } />
                <div className="module device device__laptop device__desktop">
                  <DraftCaption />
                </div>
              </div>
            </div>
          </Col>
        </Cols>

        <MobileFooter />
      </div>
    )
  }
}
